/**
 * The AI calls section's automation tab (ring R4): what the automation is
 * doing right now, why, from which campaign it rings next, and the two
 * controls a manager needs beside that (the automation's switch and "stop
 * everything").
 *
 * A HAND-WRITTEN MIRROR of the automation's status route
 * (`apps/api/src/modules/crm-calls/outbound-auto.routes.ts`, built by
 * `lib/outbound/auto/status.ts`) and of the word lists it answers with:
 * `AUTO_REASONS` and `CAMPAIGN_SKIPS` (`auto/policy.ts`), `WINDOW_REASONS`
 * (`auto/window.ts`), `PLACE_FAILURE_REASONS` (`auto/target-outcome.ts`) and
 * `ESTONIAN_HOLIDAYS` (`packages/core/src/estonianHolidays.ts`; the API's
 * `auto/holidays.ts` only re-exports it). `aiAutomation.test.ts` reads those
 * files and holds the lists against them, so a reason added on the server
 * fails a test here instead of reaching the tab as a raw key.
 *
 * The reply is read through `readAutomationStatus` rather than trusted: the
 * tab polls, and one thinner body from a server a deploy behind or ahead must
 * not take the page down. Every reason is looked up through a key helper that
 * falls back to an "unknown" wording, for the same reason.
 *
 * "Call the next one now" belongs to the campaign editor's card
 * (`aiCampaignCallNext.ts`); it is re-exported here so the tab reaches the
 * automation's whole surface through one file.
 *
 * React-free: the keys, tones, the reading and the time labels are the parts
 * worth testing, and they run under plain node.
 */
import { ApiError, api } from "./api";
import { OUTBOUND_BLOCKERS, type OutboundBlocker } from "./aiOutbound";
import type { CampaignAngle, CampaignKind } from "./aiCampaigns";
import type { TargetSkipReason } from "./aiCampaignAutomation";
import type { BusinessDay } from "./telephonyHours";
import type { StatusTone } from "../crm/ui";

export { callNextAiCampaign, callNextNote, type CallNextNote, type CallNextReply } from "./aiCampaignAutomation";

// ---------------------------------------------------------------- mirror

/** Why the automation rings or does not, in the order the policy checks. Mirrors `AUTO_REASONS`. */
export const AUTO_REASONS = [
  "go",
  "disabled",
  "autopilot_off",
  "not_ready",
  "outside_window",
  "holiday",
  "daily_cap",
  "test_daily_cap",
  "busy",
  "gap",
  "no_campaigns",
  "no_targets",
] as const;
export type AutoReason = (typeof AUTO_REASONS)[number];

/** Why one campaign is not worked now. Mirrors `CAMPAIGN_SKIPS`. */
export const CAMPAIGN_SKIPS = [
  "not_active",
  "auto_off",
  "before_start",
  "after_end",
  "no_audience",
  "blocked",
  "campaign_cap",
  "no_targets",
] as const;
export type CampaignSkip = (typeof CAMPAIGN_SKIPS)[number];

/** Where the calling window stands. Mirrors `WINDOW_REASONS`. */
export const WINDOW_REASONS = ["open", "closed_day", "holiday", "before_open", "after_close"] as const;
export type WindowReason = (typeof WINDOW_REASONS)[number];

/** Every refusal `placeOutboundAgentCall` answers with. Mirrors `PLACE_FAILURE_REASONS`. */
export const PLACE_FAILURE_REASONS = [
  "disabled",
  "not_ready",
  "no_test_numbers",
  "test_daily_cap",
  "daily_cap",
  "busy",
  "no_target_phone",
  "bad_number",
  "own_line",
  "suppressed",
  "unreachable_number",
  "opening_invalid",
  "flow_invalid",
  "not_configured",
  "db_failed",
  "twilio_failed",
  "defect_gone",
  "alert_unchecked",
  "alert_too_old",
  "alert_called",
  "site_excluded",
  "mode_changed",
  "outside_window",
  "target_unbound",
] as const;
export type PlaceFailureReason = (typeof PLACE_FAILURE_REASONS)[number];

/** The public holidays the window can skip. Mirrors `ESTONIAN_HOLIDAYS`. */
export const ESTONIAN_HOLIDAYS = [
  "new_year",
  "independence_day",
  "good_friday",
  "easter_sunday",
  "spring_day",
  "pentecost",
  "victory_day",
  "midsummer_day",
  "restoration_day",
  "christmas_eve",
  "christmas_day",
  "boxing_day",
] as const;
export type EstonianHoliday = (typeof ESTONIAN_HOLIDAYS)[number];

/** How one tick of the automation ended. Mirrors `TickOutcome` (`auto/runner.ts`). */
export const TICK_OUTCOMES = ["placed", "refused", "decided", "failed"] as const;
export type TickOutcome = (typeof TICK_OUTCOMES)[number];

/** What started a tick. Mirrors `TickReport["trigger"]`. */
export const TICK_TRIGGERS = ["schedule", "call_now"] as const;
export type TickTrigger = (typeof TICK_TRIGGERS)[number];

/**
 * Reasons only the runner names: a campaign that cannot place (no audience), a
 * tick that threw, and a second API process holding the automation's lease.
 * Worded with the automation reasons, never as a placement refusal.
 */
export const TICK_REASONS = ["no_audience", "tick_failed", "lease_held"] as const;

// ---------------------------------------------------------------- reply

/** The settings the decision was made on. */
export interface AutomationSettings {
  enabled: boolean;
  autopilot: boolean;
  testMode: boolean;
  /** Seven entries, index 0 is Sunday. */
  days: BusinessDay[];
  timezone: string;
  skipHolidays: boolean;
  minGapSec: number;
  dailyCap: number;
  testDailyCap: number;
}

export interface CampaignVerdict {
  id: string;
  skip: CampaignSkip | null;
}

export interface AutomationDecision {
  go: boolean;
  /** "go" when go. A string: a reason newer than this build still reads. */
  reason: AutoReason | (string & {});
  test: boolean;
  /** The campaign the next call comes from, when go. */
  campaignId: string | null;
  /** Non-empty only for not_ready. */
  blockers: OutboundBlocker[];
  /** When the reason can change by itself (ISO), or null. */
  until: string | null;
  campaigns: CampaignVerdict[];
}

export interface AutomationWindow {
  open: boolean;
  reason: WindowReason | (string & {});
  holiday: EstonianHoliday | null;
  nextOpenAt: string | null;
  /** Today's local date "YYYY-MM-DD" in the settings zone ("" for an unknown zone). */
  localDate: string;
}

export interface AutomationCampaign {
  id: string;
  name: string;
  angle: CampaignAngle;
  kind: CampaignKind;
  status: string;
  autoEnabled: boolean;
  startsAt: string | null;
  endsAt: string | null;
  dailyCap: number;
  usedToday: number;
  waiting: number;
  due: number;
  urgentDue: boolean;
  nextDueAt: string | null;
  skip: CampaignSkip | null;
  blockedUntil: string | null;
}

export interface TickReport {
  at: string;
  trigger: TickTrigger | (string & {});
  outcome: TickOutcome | (string & {});
  /** An AutoReason, a PlaceFailure reason, or one of `TICK_REASONS`. */
  reason: string;
  test: boolean;
  campaignId: string | null;
  targetId: string | null;
  callLogId: string | null;
  companyName: string | null;
  until: string | null;
  blockers: OutboundBlocker[];
  skipped: Array<{ targetId: string; reason: TargetSkipReason; test: boolean }>;
  refilled: Array<{ campaignId: string; added: number }>;
  clientTasks: number;
  settled: number;
  /** Consecutive identical reports collapsed into this one. */
  repeats: number;
  /** The pressed campaign's own verdict when "call next now" found it could not be worked. */
  campaignSkip: CampaignSkip | null;
}

/** GET `/automation`. Read-only on the server: no refill, no settle, no writes. */
export interface AutomationStatus {
  now: string;
  settings: AutomationSettings;
  decision: AutomationDecision;
  window: AutomationWindow;
  today: { real: number; test: number; realCap: number; testCap: number };
  /** AI outbound calls not ended yet. */
  running: number;
  lastStartedAt: string | null;
  campaigns: AutomationCampaign[];
  lastTick: TickReport | null;
  /** Newest first. */
  history: TickReport[];
  /** A tick or "call next now" is running on the server right now. */
  ticking: boolean;
}

/** POST `/stop`: calls hung up, hang-ups that failed, and calls with no Twilio id yet (refused on answer). */
export interface StopReply {
  ended: number;
  failed: number;
  pending: number;
}

// ---------------------------------------------------------------- requests

const BASE = "/admin/crm/calls/ai-outbound";

/** The tab's whole state in one read; `null` when the body is not one this page can draw. */
export async function getAiAutomation(): Promise<AutomationStatus | null> {
  return readAutomationStatus(await api.get<unknown>(`${BASE}/automation`));
}

/**
 * "Stop everything": the server switches AI calling and the automation off,
 * waits for a running tick, then hangs up the AI calls still going.
 */
export async function stopAiOutbound(): Promise<StopReply> {
  const raw = await api.post<Partial<StopReply> | null>(`${BASE}/stop`);
  return { ended: count(raw?.ended), failed: count(raw?.failed), pending: count(raw?.pending) };
}

/** Every refusal the stop button names. */
export const STOP_ERRORS = ["stop_failed", "forbidden", "failed"] as const;
export type StopError = (typeof STOP_ERRORS)[number];

export function stopError(err: unknown): StopError {
  if (!(err instanceof ApiError)) return "failed";
  if (err.status === 403) return "forbidden";
  const code = (err.body as { error?: unknown } | null)?.error;
  return code === "stop_failed" ? "stop_failed" : "failed";
}

// ---------------------------------------------------------------- reading

type Raw = Record<string, unknown>;

const isObject = (v: unknown): v is Raw => Boolean(v) && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const count = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.trunc(v) : 0);
const list = <T>(v: unknown, read: (x: unknown) => T | null): T[] =>
  Array.isArray(v) ? v.map(read).filter((x): x is T => x !== null) : [];
const strings = (v: unknown): string[] => list(v, (x) => (typeof x === "string" ? x : null));

function readTick(raw: unknown): TickReport | null {
  if (!isObject(raw) || typeof raw.at !== "string" || typeof raw.outcome !== "string" || typeof raw.reason !== "string") {
    return null;
  }
  return {
    at: raw.at,
    trigger: str(raw.trigger) ?? "schedule",
    outcome: raw.outcome,
    reason: raw.reason,
    test: raw.test === true,
    campaignId: str(raw.campaignId),
    targetId: str(raw.targetId),
    callLogId: str(raw.callLogId),
    companyName: typeof raw.companyName === "string" && raw.companyName.trim() ? raw.companyName : null,
    until: str(raw.until),
    blockers: strings(raw.blockers) as OutboundBlocker[],
    skipped: list(raw.skipped, (x) =>
      isObject(x) && typeof x.targetId === "string" && typeof x.reason === "string"
        ? { targetId: x.targetId, reason: x.reason as TargetSkipReason, test: x.test === true }
        : null,
    ),
    refilled: list(raw.refilled, (x) =>
      isObject(x) && typeof x.campaignId === "string" ? { campaignId: x.campaignId, added: count(x.added) } : null,
    ),
    clientTasks: count(raw.clientTasks),
    settled: count(raw.settled),
    repeats: count(raw.repeats),
    campaignSkip: (str(raw.campaignSkip) as CampaignSkip | null) ?? null,
  };
}

function readCampaign(raw: unknown): AutomationCampaign | null {
  if (!isObject(raw) || typeof raw.id !== "string") return null;
  return {
    id: raw.id,
    name: typeof raw.name === "string" ? raw.name : "",
    angle: raw.angle === "alert" ? "alert" : "offer",
    kind: raw.kind === "cold" ? "cold" : "warm",
    status: typeof raw.status === "string" ? raw.status : "",
    autoEnabled: raw.autoEnabled === true,
    startsAt: str(raw.startsAt),
    endsAt: str(raw.endsAt),
    dailyCap: count(raw.dailyCap),
    usedToday: count(raw.usedToday),
    waiting: count(raw.waiting),
    due: count(raw.due),
    urgentDue: raw.urgentDue === true,
    nextDueAt: str(raw.nextDueAt),
    skip: (str(raw.skip) as CampaignSkip | null) ?? null,
    blockedUntil: str(raw.blockedUntil),
  };
}

/**
 * The status route's answer as this page draws it, or null when the parts the
 * page cannot do without (settings, decision, window) are missing. Lists that
 * are absent read as empty, counts that are not numbers as 0.
 */
export function readAutomationStatus(raw: unknown): AutomationStatus | null {
  if (!isObject(raw) || !isObject(raw.settings) || !isObject(raw.decision) || !isObject(raw.window)) return null;
  const s = raw.settings;
  const d = raw.decision;
  const w = raw.window;
  const today = isObject(raw.today) ? raw.today : {};
  return {
    now: str(raw.now) ?? new Date().toISOString(),
    settings: {
      enabled: s.enabled === true,
      autopilot: s.autopilot === true,
      // Unknown reads as test: the safer of the two things to tell an operator.
      testMode: s.testMode !== false,
      days: list(s.days, (x) =>
        isObject(x) ? { open: x.open === true, from: count(x.from), to: count(x.to) } : null,
      ),
      timezone: typeof s.timezone === "string" ? s.timezone : "",
      skipHolidays: s.skipHolidays === true,
      minGapSec: count(s.minGapSec),
      dailyCap: count(s.dailyCap),
      testDailyCap: count(s.testDailyCap),
    },
    decision: {
      go: d.go === true,
      reason: typeof d.reason === "string" ? d.reason : "",
      test: d.test !== false,
      campaignId: str(d.campaignId),
      blockers: strings(d.blockers) as OutboundBlocker[],
      until: str(d.until),
      campaigns: list(d.campaigns, (x) =>
        isObject(x) && typeof x.id === "string" ? { id: x.id, skip: (str(x.skip) as CampaignSkip | null) ?? null } : null,
      ),
    },
    window: {
      open: w.open === true,
      reason: typeof w.reason === "string" ? w.reason : "",
      holiday: (str(w.holiday) as EstonianHoliday | null) ?? null,
      nextOpenAt: str(w.nextOpenAt),
      localDate: typeof w.localDate === "string" ? w.localDate : "",
    },
    today: { real: count(today.real), test: count(today.test), realCap: count(today.realCap), testCap: count(today.testCap) },
    running: count(raw.running),
    lastStartedAt: str(raw.lastStartedAt),
    campaigns: list(raw.campaigns, readCampaign),
    lastTick: readTick(raw.lastTick),
    history: list(raw.history, readTick),
    ticking: raw.ticking === true,
  };
}

// ---------------------------------------------------------------- words

const AUTO: ReadonlySet<string> = new Set<string>([...AUTO_REASONS, ...TICK_REASONS]);
const PLACE: ReadonlySet<string> = new Set<string>(PLACE_FAILURE_REASONS);
const SKIPS: ReadonlySet<string> = new Set<string>(CAMPAIGN_SKIPS);
const BLOCKERS: ReadonlySet<string> = new Set<string>(OUTBOUND_BLOCKERS);

/**
 * The catalogue key a reason is worded with: an automation reason under
 * `aiCalls.automation.reason`, a placement refusal under `placeReason`, and
 * anything this build does not know as `reason.unknown`.
 *
 * Six codes are both (disabled, not_ready, busy, the two caps, outside_window):
 * the policy says them before a call and `place.ts` says them again as its own
 * last word. `outcome` settles which one a tick report meant: a refused tick
 * carries a placement reason, every other tick an automation reason.
 */
export function reasonKey(reason: string, outcome?: string): string {
  if (outcome === "refused" && PLACE.has(reason)) return `aiCalls.automation.placeReason.${reason}`;
  if (AUTO.has(reason)) return `aiCalls.automation.reason.${reason}`;
  if (PLACE.has(reason)) return `aiCalls.automation.placeReason.${reason}`;
  return "aiCalls.automation.reason.unknown";
}

/**
 * A tick report's key. The pressed campaign's own skip wins, the way the
 * campaign card's note words it (`callNextReasonKey`): "no active campaign"
 * is true of the automation but hides why THIS campaign could not be worked.
 */
export function tickReasonKey(report: Pick<TickReport, "outcome" | "reason" | "campaignSkip">): string {
  if (report.campaignSkip && SKIPS.has(report.campaignSkip)) return campaignSkipKey(report.campaignSkip);
  return reasonKey(report.reason, report.outcome);
}

/** A campaign's verdict as a key; an unknown skip as `campaignSkip.unknown`. */
export function campaignSkipKey(skip: string): string {
  return `aiCalls.automation.campaignSkip.${SKIPS.has(skip) ? skip : "unknown"}`;
}

/** A readiness gap as a short label key; an unknown one as `blocker.unknown`. */
export function blockerKey(blocker: string): string {
  return `aiCalls.automation.blocker.${BLOCKERS.has(blocker) ? blocker : "unknown"}`;
}

/**
 * How loudly the decision is drawn. Green when the automation rings or a call
 * is on the line; amber when only time has to pass (window, holiday, caps,
 * gap); red when something has to be fixed; blue when there is nothing to
 * ring; quiet when it is switched off, or the reason is unknown.
 */
export function decisionTone(decision: Pick<AutomationDecision, "go" | "reason">): StatusTone {
  if (decision.go) return "ok";
  switch (decision.reason) {
    case "busy":
      return "ok";
    case "outside_window":
    case "holiday":
    case "daily_cap":
    case "test_daily_cap":
    case "gap":
      return "wt";
    case "not_ready":
      return "lt";
    case "no_campaigns":
    case "no_targets":
      return "info";
    default:
      return "mut";
  }
}

/** A tick's outcome as a pill tone: a call green, a refusal amber, a quiet decision grey, a failure red. */
export function outcomeTone(outcome: string): StatusTone {
  if (outcome === "placed") return "ok";
  if (outcome === "refused") return "wt";
  if (outcome === "failed") return "lt";
  return "mut";
}

/** How often the tab re-reads the status while it is visible. The automation ticks every 30 s. */
export const AUTOMATION_POLL_MS = 15_000;
